import { Record as record, List } from 'immutable';
import _ from 'lodash';

import trainTime from '../../../../src/lib/TrainTime';
import config from '../../../../src/config';
import {
    CompensationClaimType,
    CompensationTicket
} from '../../../../src/constants/AppConstants';
import { unique, sum } from '../../../../src/helpers/Immutable';
import { getDefaults, getResolvers } from '../../../../src/helpers/Records';
import {
    construct,
    resolve,
    resolveAll,
    fields
} from '../../../../src/helpers/LinkMapper';

import Customer from './Customer';
import Address from '../../../../src/records/customer/Address';
import Payment from '../Payment';
import Period from './Period';
import TravelWeek from './TravelWeek';

const fieldSchema = {
    userId: [fields.number],
    userEmail: [fields.string],
    customer: [resolve.as(Customer), new Customer()],
    address: [resolve.as(Address), new Address({ contactName: 'n/a' })],
    storedAddress: [resolve.as(Address)],
    allWeeks: [resolveAll.as(Period), new List()],
    travelWeeks: [resolveAll.as(TravelWeek), List.of(new TravelWeek())],
    payment: [resolve.as(Payment), new Payment()],
    smartcardIsrn: [fields.string]
};

export default class CompensationNonSeasonClaim extends record(
    getDefaults(fieldSchema)
) {
    static fromJSON(json = {}) {
        return construct(
            CompensationNonSeasonClaim,
            json,
            getResolvers(fieldSchema)
        );
    }

    static create({ startDate, endDate, startDayOfWeek = 1 } = {}) {
        let allWeeks = new List();
        if (
            trainTime.isTrainTime(startDate) &&
            trainTime.isTrainTime(endDate)
        ) {
            allWeeks = getSchemeWeeks({ startDate, endDate, startDayOfWeek }); // eslint-disable-line no-use-before-define
        }

        return new CompensationNonSeasonClaim({ allWeeks });
    }

    get type() {
        return CompensationClaimType.NON_SEASON;
    }

    setUser(user) {
        let updated = this;
        if (this.customer.isUntouched()) {
            updated = this.merge({
                customer: this.customer.initialiseWithUser(user),
                userId: user.id,
                userEmail: user.email
            });
        }
        return updated;
    }

    setSmartcard(smartcard) {
        return this.set('smartcardIsrn', _.result(smartcard, 'getCardNumber'));
    }

    get schemeWeeks() {
        const usedPeriods = this.travelWeeks
            .map(_.property('period'))
            .filter(Boolean)
            .map(_.property('id'))
            .toSet();
        const isUsed = week => usedPeriods.has(week.id);
        return this.allWeeks.map(week => week.set('used', isUsed(week)));
    }

    getRoutes() {
        const allRoutes = this.travelWeeks.flatMap(week => week.getRoutes());
        return unique(allRoutes, _.property('internalId'));
    }

    getOysterCards() {
        const oysterCards = this.travelWeeks.flatMap(week => week.getOysterCards());
        return unique(oysterCards, _.identity);
    }

    canAddTravelWeek() {
        const { travelWeeks } = this;
        const max = this.hasCarnetTickets() ?
            config.get('compensationMaxWeeksWithCarnet') :
            config.get('compensationMaxWeeks');
        return (
            travelWeeks.size < max &&
            travelWeeks.every(week => week.isComplete())
        );
    }

    canSubmitTickets() {
        const { travelWeeks } = this;
        const max = this.hasCarnetTickets() ?
            config.get('compensationMaxWeeksWithCarnet') :
            config.get('compensationMaxWeeks');
        return (
            travelWeeks.size >= config.get('compensationMinWeeks') &&
            travelWeeks.size <= max &&
            travelWeeks.every(week => week.isComplete())
        );
    }

    hasTickets() {
        return this.travelWeeks.some(week => week.dayTickets.size > 0);
    }

    hasCarnetTickets() {
        return this.travelWeeks.some(week =>
            week.dayTickets.some(dayTickets =>
                dayTickets.hasTypeId(CompensationTicket.CARNET)
            )
        );
    }

    canAddLevel2(travelWeek) {
        const withLevel2 = this.travelWeeks.filter(week =>
            week.hasLevel2Tickets()
        );
        return (
            withLevel2.size < config.get('compensationMaxWeeksLevel2') ||
            withLevel2.includes(travelWeek)
        );
    }

    addTravelWeek() {
        return this.set('travelWeeks', this.travelWeeks.push(new TravelWeek()));
    }

    removeTravelWeek(travelWeek) {
        return this.set(
            'travelWeeks',
            this.travelWeeks.filterNot(({ id }) => id === travelWeek.id)
        );
    }

    updateTravelWeek(updatedWeek) {
        return this.set(
            'travelWeeks',
            this.travelWeeks.map(
                week => (week.id === updatedWeek.id ? updatedWeek : week)
            )
        );
    }

    getTravelWeekById(id) {
        return this.travelWeeks.find(week => week.id === id);
    }

    updateEvidence({ week, date, direction }, ticketEvidence, values) {
        const currentWeek = this.getTravelWeekById(week.id);
        const dayEvidence = currentWeek.getEvidence(date);
        if (
            !dayEvidence ||
            dayEvidence.getIn([direction, 'id']) !== ticketEvidence.id
        ) {
            return this;
        }
        return this.updateTravelWeek(
            currentWeek.setEvidence(
                date,
                dayEvidence.mergeIn([direction], values)
            )
        );
    }

    getAllowedBookSize(date) {
        const maxBookSize = config.get('compensationMaxTotalBookSize');
        if (!date) {
            return maxBookSize;
        }
        const carnetTickets = this.travelWeeks
            .flatMap(week => {
                const otherDays = week.getOtherDaysWithTickets(date);
                return otherDays.map(day => week.getTickets(day));
            })
            .filter(dayTickets =>
                dayTickets.hasTypeId(CompensationTicket.CARNET)
            )
            .map(({ outward }) => outward);
        const currentTickets = sum(carnetTickets, 'bookSize');
        return maxBookSize - currentTickets;
    }

    serializeClaims() {
        return this.travelWeeks
            .sortBy(week => week.period.id)
            .flatMap(week => week.serializeClaims());
    }
}

function getSchemeWeeks({ startDate, endDate, startDayOfWeek }) {
    let startOfWeek = startDate.clone().day(startDayOfWeek);
    if (startOfWeek.isBefore(startDate)) {
        startOfWeek.add(1, 'week');
    }

    let weeks = [];
    while (startOfWeek.isBefore(endDate)) {
        const endOfWeek = trainTime.min(
            startOfWeek
                .clone()
                .add(1, 'week')
                .subtract(1, 'second'),
            endDate.clone().endOf('day')
        );
        weeks.push(
            new Period({
                start: startOfWeek.clone(),
                end: endOfWeek
            })
        );
        startOfWeek.add(1, 'week');
    }
    return new List(weeks);
}
